import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FaBell, FaBolt, FaCalendarAlt, FaCheck, FaEdit, FaEye, FaFilePdf, FaFolderOpen, FaKeyboard, FaPlus, FaSearch } from 'react-icons/fa';

import { AppButton, ButtonGroup } from '../_Templates/_UI/Button/_index';
import {
    Accordion, Avatar, AvatarGroup, Badge, Breadcrumbs, Card, Chip, Divider, EmptyState,
    Pagination, Progress, Skeleton, SkeletonRow, Spinner, Stepper, Tabs, Tooltip,
} from '../_Templates/_UI/Kit/_index';
import { StatusBadge } from '../_Templates/_UI/Status/_index';
import { MetricCard, MetricGrid } from '../_Templates/_UI/Metrics/_index';
import { ChartArea, ChartBar, ChartBarH, ChartDonut, ChartFunnel, ChartGauge, ChartLine, Sparkline } from '../_Templates/_UI/Charts/_index';
import { AppCodeBlock, onPrettyJson } from '../_Templates/_UI/CodeBlock/_index';
import { DetailItem, DetailSection, DetailView } from '../_Templates/_UI/Detail/_index';
import { FilterBar } from '../_Templates/_UI/Filters/_index';
import { InlineEdit } from '../_Templates/_UI/InlineEdit/_index';
import { PageHeader, PageHeaderMeta } from '../_Templates/_UI/PageHeader/_index';
import { Timeline, TimelineItem } from '../_Templates/_UI/Timeline/_index';
import { AppTree } from '../_Templates/_UI/Tree/_index';
import { DataTable, TableActionMenu, TableGrid, TablePerPage, TableViewToggle } from '../_Templates/_UI/Table/_index';
import { AppCalendar } from '../_Templates/_UI/Calendar/_index';
import { AppGallery } from '../_Templates/_UI/Gallery/_index';
import { AppKanban } from '../_Templates/_UI/Kanban/_index';
import { useKanban } from '../_Templates/_UI/Kanban/useKanban';
import { AppModal } from '../_Templates/_UI/Modal/_index';
import { AppPopover, PopoverDivider, PopoverItem, PopoverTitle } from '../_Templates/_UI/Popover/_index';
import { ReadOnlyInfo, ReadOnlyModalShell, ReadOnlySectionNav } from '../_Templates/_UI/ReadOnly/_index';
import { SaveBar, SaveBarStamp } from '../_Templates/_UI/SaveBar/_index';
import { DataNotice, ScreenError, ScreenForbidden, ScreenMaintenance, ScreenNotFound, ScreenState } from '../_Templates/_UI/Screen/_index';
import { onBuildBreadcrumbJsonLd, onBuildOrganizationJsonLd } from '../_Templates/_UI/Seo/_index';
import { ShortcutKeys, ShortcutsHelp } from '../_Templates/_UI/Shortcuts/_index';
import { SplitPane } from '../_Templates/_UI/Split/_index';
import { AppUploader, useUploader } from '../_Templates/_UI/Uploader/_index';
import { VirtualList, VirtualTable } from '../_Templates/_UI/Virtual/_index';
import { AppWizard, useWizard } from '../_Templates/_UI/Wizard/_index';
import { AppChat, useChat } from '../_Templates/_UI/Chat/_index';
import { AppSupportChat, useSupportChat } from '../_Templates/_UI/SupportChat/_index';
import { AppCallCenterChat, useCallCenterChat } from '../_Templates/_UI/CallCenterChat/_index';
import { AppCommandPalette } from '../_Templates/_UI/CommandPalette/_index';
import { AlertReactBootstrap } from '../_Templates/_UI/Alerts/AlertReactBootstrap';
import { AppToastFixed } from '../_Templates/_UI/Alerts/AppToastFixed';
import { FONT_TEMPLATES } from '../_Templates/_UI/Fonts/_FontTemplates';
import { RICHTEXT_TEMPLATES } from '../_Templates/_UI/RichText/_RichTextTemplates';
import { TABLE_TEMPLATES } from '../_Templates/_UI/Table/_TableTemplates';
import { CONFIRM_TEMPLATES } from '../_Templates/_UI/Confirm/_ConfirmTemplates';
import { confirm } from '../_Templates/_UI/Confirm/AppConfirmHost';

import { Form, FormActions, FormButton, FormGrid, FormSection } from '../_Templates/_UI/Form/FormLayout';
import { FormWizard } from '../_Templates/_UI/Form/FormWizard';
import { FieldInput } from '../_Templates/_UI/Form/_Fields/FieldInput';
import { FieldNumber } from '../_Templates/_UI/Form/_Fields/FieldNumber';
import { FieldTextarea } from '../_Templates/_UI/Form/_Fields/FieldTextarea';
import { FieldSelect } from '../_Templates/_UI/Form/_Fields/FieldSelect';
import { FieldCheckbox } from '../_Templates/_UI/Form/_Fields/FieldCheckbox';
import { FieldSwitch } from '../_Templates/_UI/Form/_Fields/FieldSwitch';
import { FieldRadioGroup } from '../_Templates/_UI/Form/_Fields/FieldRadioGroup';
import { FieldRange } from '../_Templates/_UI/Form/_Fields/FieldRange';
import { FieldRating } from '../_Templates/_UI/Form/_Fields/FieldRating';
import { FieldSegmented } from '../_Templates/_UI/Form/_Fields/FieldSegmented';
import { FieldTags } from '../_Templates/_UI/Form/_Fields/FieldTags';
import { FieldColor } from '../_Templates/_UI/Form/_Fields/FieldColor';
import { FieldDuration } from '../_Templates/_UI/Form/_Fields/FieldDuration';
import { FieldMonthYear } from '../_Templates/_UI/Form/_Fields/FieldMonthYear';
import { FieldAutocomplete } from '../_Templates/_UI/Form/_Fields/FieldAutocomplete';
import { FieldPasswordStrength } from '../_Templates/_UI/Form/_Fields/FieldPasswordStrength';
import { FieldWeekdays } from '../_Templates/_UI/Form/_Fields/FieldWeekdays';

const BLOCK = 'LStudio__demo_templates';

const FORM_EXPORTS = [
    'Form', 'FormSection', 'FormGrid', 'FormActions', 'FormButton', 'FormWizard', 'FormFloatingBack',
    'FieldInput', 'FieldNumber', 'FieldTextarea', 'FieldSelect', 'FieldCheckbox', 'FieldSwitch', 'FieldRadioGroup',
    'FieldRange', 'FieldRangeDual', 'FieldCounter', 'FieldRating', 'FieldSegmented', 'FieldCheckboxGroup', 'FieldCards',
    'FieldNps', 'FieldWeekdays', 'FieldMatrix', 'FieldOtp', 'FieldTags', 'FieldColor', 'FieldMasked', 'FieldAvatar',
    'FieldSlug', 'FieldCode', 'FieldNumberUnit', 'FieldDuration', 'FieldMonthYear', 'FieldAutocomplete',
    'FieldPasswordStrength', 'FieldIconPicker', 'FieldSignature', 'FieldEmoji', 'FieldMentions', 'FieldImages',
    'FieldRepeater', 'FieldTransfer', 'FieldKeyValue', 'FieldTree', 'FieldSchedule', 'FieldAddress', 'FieldCreditCard',
    'FieldSortable', 'FieldSelectSearch', 'FieldMultiSelect', 'FieldSearchAsync', 'FieldDate', 'FieldDateRange',
    'FieldPhone', 'FieldCurrency', 'FieldFile', 'FieldRichText', 'FieldMap', 'FieldVideoUrl', 'FieldImageCrop',
    'FieldQrScanner', 'FieldQrGenerator', 'FieldVideoUpload', 'FieldChapters', 'FieldSubtitles', 'FieldCurriculum',
];

const TEMPLATE_CATALOG = [
    { id: 'alerts', label: 'Alerts', exports: ['AlertReactBootstrap', 'AppAlertHost', 'AppToastFixed', 'AppToaster'] },
    { id: 'button', label: 'Button', exports: ['AppButton', 'ButtonGroup'] },
    { id: 'calendar', label: 'Calendar', exports: ['AppCalendar'] },
    { id: 'chat', label: 'Chat', exports: ['AppChat', 'useChat'] },
    { id: 'supportchat', label: 'SupportChat', exports: ['AppSupportChat', 'useSupportChat'] },
    { id: 'callcenterchat', label: 'CallCenterChat', exports: ['AppCallCenterChat', 'useCallCenterChat'] },
    { id: 'charts', label: 'Charts', exports: ['ChartArea', 'ChartBar', 'ChartBarH', 'ChartDonut', 'ChartFunnel', 'ChartGauge', 'ChartLine', 'Sparkline'] },
    { id: 'codeblock', label: 'CodeBlock', exports: ['AppCodeBlock', 'onHighlight', 'onPrettyJson'] },
    { id: 'commandpalette', label: 'CommandPalette', exports: ['AppCommandPalette', 'useCommandPalette'] },
    { id: 'confirm', label: 'Confirm', exports: ['AppConfirmHost', 'confirm', 'CONFIRM_TEMPLATES'] },
    { id: 'detail', label: 'Detail', exports: ['DetailView', 'DetailSection', 'DetailItem'] },
    { id: 'filters', label: 'Filters', exports: ['FilterBar'] },
    { id: 'fonts', label: 'Fonts', exports: ['FONT_TEMPLATES', 'FONT_TEMPLATE_IDS', 'onResolveFontTemplate', 'onLoadFontTemplate'] },
    { id: 'form', label: 'Form', exports: FORM_EXPORTS },
    { id: 'gallery', label: 'Gallery', exports: ['AppGallery', 'AppLightbox'] },
    { id: 'inlineedit', label: 'InlineEdit', exports: ['InlineEdit'] },
    { id: 'kanban', label: 'Kanban', exports: ['AppKanban', 'useKanban', 'onNextOrder', 'onOrderBetween'] },
    { id: 'kit', label: 'Kit', exports: ['Badge', 'Chip', 'Card', 'Avatar', 'AvatarGroup', 'Divider', 'Accordion', 'Tooltip', 'Skeleton', 'SkeletonRow', 'EmptyState', 'Spinner', 'Progress', 'Tabs', 'Breadcrumbs', 'Pagination', 'Stepper'] },
    { id: 'metrics', label: 'Metrics', exports: ['MetricCard', 'MetricGrid'] },
    { id: 'modal', label: 'Modal', exports: ['AppModal', 'useModal'] },
    { id: 'pageheader', label: 'PageHeader', exports: ['PageHeader', 'PageHeaderMeta'] },
    { id: 'pdf', label: 'Pdf', exports: ['createPdfComposer', 'PDF_APPEARANCE_DEFAULT'] },
    { id: 'popover', label: 'Popover', exports: ['AppPopover', 'PopoverItem', 'PopoverDivider', 'PopoverTitle', 'usePopover'] },
    { id: 'readonly', label: 'ReadOnly', exports: ['ReadOnlyInfo', 'ReadOnlyModalShell', 'ReadOnlySectionNav'] },
    { id: 'richtext', label: 'RichText', exports: ['RICHTEXT_TEMPLATES', 'onResolveRichTextTemplate'] },
    { id: 'savebar', label: 'SaveBar', exports: ['SaveBar', 'SaveBarStamp', 'useSaveBar'] },
    { id: 'screen', label: 'Screen', exports: ['ScreenState', 'PageLoader', 'ScreenNotFound', 'ScreenForbidden', 'ScreenError', 'ScreenMaintenance', 'DataNotice'] },
    { id: 'seo', label: 'Seo', exports: ['AppSeo', 'onBuildOrganizationJsonLd', 'onBuildBreadcrumbJsonLd'] },
    { id: 'shortcuts', label: 'Shortcuts', exports: ['ShortcutsHelp', 'ShortcutKeys', 'useShortcuts'] },
    { id: 'split', label: 'Split', exports: ['SplitPane'] },
    { id: 'status', label: 'Status', exports: ['StatusBadge', 'STATUS_MAP_DEFAULT', 'onBuildStatusMap', 'onResolveStatus'] },
    { id: 'table', label: 'Table', exports: ['DataTable', 'TableHeader', 'TableActionMenu', 'TableImportModal', 'TablePerPage', 'TableViewToggle', 'TableGrid', 'TABLE_TEMPLATES'] },
    { id: 'timeline', label: 'Timeline', exports: ['Timeline', 'TimelineItem'] },
    { id: 'tree', label: 'Tree', exports: ['AppTree'] },
    { id: 'uploader', label: 'Uploader', exports: ['AppUploader', 'useUploader', 'UPLOAD_STATUS', 'onFormatBytes'] },
    { id: 'virtual', label: 'Virtual', exports: ['VirtualList', 'VirtualTable', 'useVirtualList'] },
    { id: 'wizard', label: 'Wizard', exports: ['AppWizard', 'useWizard'] },
];

const CHART_DATA = [
    { label: 'Ene', value: 28, tone: 'accent' }, { label: 'Feb', value: 45, tone: 'ok' },
    { label: 'Mar', value: 34, tone: 'warn' }, { label: 'Abr', value: 61, tone: 'accent' },
    { label: 'May', value: 52, tone: 'neutral' }, { label: 'Jun', value: 74, tone: 'ok' },
];

const TABLE_COLUMNS = [
    { key: 'name', label: 'Nombre' },
    { key: 'email', label: 'Correo' },
    { key: 'role', label: 'Rol' },
    { key: 'status', label: 'Estado', render: (row) => <StatusBadge status={row.status} /> },
];

const TABLE_ROWS = [
    { id: 1, name: 'Ana Torres', email: 'ana@example.com', role: 'Administradora', status: 'active' },
    { id: 2, name: 'Bruno Díaz', email: 'bruno@example.com', role: 'Editor', status: 'pending' },
    { id: 3, name: 'Carla Ruiz', email: 'carla@example.com', role: 'Soporte', status: 'inactive' },
    { id: 4, name: 'Diego López', email: 'diego@example.com', role: 'Analista', status: 'active' },
    { id: 5, name: 'Eva Molina', email: 'eva@example.com', role: 'Ventas', status: 'pending' },
];

const TREE_NODES = [
    { id: 'app', label: 'App', children: [
        { id: 'web', label: 'WEB', children: [
            { id: 'templates', label: '_Templates', children: [
                { id: 'ui', label: '_UI', children: [
                    { id: 'button-node', label: 'Button', isLeaf: true },
                    { id: 'table-node', label: 'Table', isLeaf: true },
                    { id: 'form-node', label: 'Form', isLeaf: true },
                ] },
            ] },
        ] },
    ] },
];

const onSvgSeed = (label, color) => `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="800" height="520"><rect width="100%" height="100%" fill="${color}"/><circle cx="640" cy="110" r="120" fill="rgba(255,255,255,.12)"/><circle cx="130" cy="420" r="180" fill="rgba(0,0,0,.10)"/><text x="50%" y="50%" fill="white" font-family="Arial" font-size="54" text-anchor="middle" dominant-baseline="middle">${label}</text></svg>`)}`;

const GALLERY_ITEMS = [
    { id: 1, src: onSvgSeed('Proyecto Alpha', '#4c5bd4'), alt: 'Proyecto Alpha', title: 'Proyecto Alpha', caption: 'Dashboard SaaS' },
    { id: 2, src: onSvgSeed('Landing Beta', '#7a4fb5'), alt: 'Landing Beta', title: 'Landing Beta', caption: 'Marketing' },
    { id: 3, src: onSvgSeed('Mobile Gamma', '#16836f'), alt: 'Mobile Gamma', title: 'Mobile Gamma', caption: 'Aplicación móvil' },
    { id: 4, src: onSvgSeed('CRM Delta', '#b05c2f'), alt: 'CRM Delta', title: 'CRM Delta', caption: 'Gestión comercial' },
];

const DemoSection = ({ title, description, children }) => (
    <section className={`${BLOCK}__demo_section`}>
        <div className={`${BLOCK}__demo_head`}>
            <h3>{title}</h3>
            {description && <p>{description}</p>}
        </div>
        <div className={`${BLOCK}__demo_body`}>{children}</div>
    </section>
);

const Inventory = ({ item }) => (
    <div className={`${BLOCK}__inventory`}>
        {item.exports.map((name) => <code key={name}>{name}</code>)}
    </div>
);

const SeedInspector = ({ title, value }) => (
    <div className={`${BLOCK}__seed`}>
        <strong>{title}</strong>
        <pre>{typeof value === 'string' ? value : JSON.stringify(value, null, 2)}</pre>
    </div>
);

const AlertsPreview = () => {
    const [useNameToastState, setNameToastState] = useState(false);
    return (
        <>
            <DemoSection title="Alerts inline" description="Estados reales con contenido seed.">
                <div className={`${BLOCK}__stack`}>
                    <AlertReactBootstrap type="success" title="Operación completada" show>El registro se guardó correctamente.</AlertReactBootstrap>
                    <AlertReactBootstrap type="warning" title="Atención" show>Quedan campos opcionales sin completar.</AlertReactBootstrap>
                    <AlertReactBootstrap type="danger" title="Error de ejemplo" show>No se pudo procesar una acción ficticia.</AlertReactBootstrap>
                </div>
            </DemoSection>
            <DemoSection title="Toast fijo" description="Probá el toast flotante del template.">
                <AppButton icon={<FaBell />} onClick={() => setNameToastState(true)}>Mostrar toast seed</AppButton>
                <AppToastFixed show={useNameToastState} type="success" title="¡Listo!" message="Toast de demostración con datos falsos." duration={5000} actionLabel="Ver detalle" onClose={() => setNameToastState(false)} />
            </DemoSection>
        </>
    );
};

const ButtonPreview = () => (
    <DemoSection title="Button" description="Variantes, tonos, tamaños y agrupación.">
        <div className={`${BLOCK}__row`}>
            <AppButton>Principal</AppButton><AppButton variant="outline">Outline</AppButton>
            <AppButton variant="ghost">Ghost</AppButton><AppButton tone="danger">Eliminar</AppButton>
            <AppButton tone="ok" icon={<FaCheck />}>Confirmar</AppButton><AppButton isLoading>Cargando</AppButton>
        </div>
        <ButtonGroup isAttached><AppButton variant="outline">Anterior</AppButton><AppButton variant="outline">Siguiente</AppButton></ButtonGroup>
    </DemoSection>
);

const CalendarPreview = () => {
    const [useNameCalendarViewState, setNameCalendarViewState] = useState('month');
    const TODAY = new Date();
    const EVENTS = [
        { id: 1, date: new Date(TODAY.getFullYear(), TODAY.getMonth(), 4), title: 'Reunión de equipo', tone: 'accent', meta: '10:00 · Meet' },
        { id: 2, date: new Date(TODAY.getFullYear(), TODAY.getMonth(), 8), title: 'Entrega sprint', tone: 'ok', meta: '18:00' },
        { id: 3, date: new Date(TODAY.getFullYear(), TODAY.getMonth(), 15), title: 'Demo cliente', tone: 'warn', meta: '15:30' },
        { id: 4, date: new Date(TODAY.getFullYear(), TODAY.getMonth(), 15), title: 'Seguimiento', tone: 'neutral', meta: '17:00' },
        { id: 5, date: new Date(TODAY.getFullYear(), TODAY.getMonth(), 22), title: 'Publicación', tone: 'accent', meta: 'Todo el día' },
    ];
    return <DemoSection title="Calendar" description="Calendario real con eventos seed y cambio Mes/Agenda."><AppCalendar events={EVENTS} value={TODAY} view={useNameCalendarViewState} onChangeView={setNameCalendarViewState} /></DemoSection>;
};

const ChartsPreview = () => (
    <>
        <DemoSection title="Charts · comparación y tendencia">
            <div className={`${BLOCK}__chart_grid`}><ChartBar data={CHART_DATA} showValues /><ChartLine labels={CHART_DATA.map((item) => item.label)} series={[{ label: 'Ventas', values: CHART_DATA.map((item) => item.value) }]} /></div>
        </DemoSection>
        <DemoSection title="Charts · resto del kit">
            <div className={`${BLOCK}__chart_grid`}>
                <ChartArea labels={['Ene', 'Feb', 'Mar', 'Abr']} series={[{ label: 'Web', values: [20, 38, 45, 66], tone: 'accent' }, { label: 'Mobile', values: [12, 25, 35, 48], tone: 'ok' }]} />
                <ChartBarH data={[{ label: 'React', value: 88 }, { label: 'Node', value: 76 }, { label: 'PHP', value: 62 }, { label: 'NestJS', value: 54 }]} />
                <ChartDonut data={[{ label: 'Web', value: 52, tone: 'accent' }, { label: 'Mobile', value: 28, tone: 'ok' }, { label: 'API', value: 20, tone: 'warn' }]} centerLabel="Total" centerValue="100" />
                <ChartGauge value={78} label="Objetivo" tone="ok" />
                <ChartFunnel steps={[{ label: 'Visitas', value: 1200 }, { label: 'Leads', value: 640 }, { label: 'Demo', value: 280 }, { label: 'Ventas', value: 96 }]} />
                <div className={`${BLOCK}__spark_wrap`}><strong>Sparkline</strong><Sparkline values={[12, 24, 18, 38, 41, 55, 49, 66]} /></div>
            </div>
        </DemoSection>
    </>
);

const CodePreview = () => (
    <DemoSection title="CodeBlock" description="Código y JSON formateado.">
        <AppCodeBlock title="Configuración seed" language="js" code={'const APP = {\n    name: "BoilerPlate",\n    reusable: true,\n    modules: 12,\n};'} />
        <SeedInspector title="onPrettyJson" value={onPrettyJson({ ok: true, records: 5, mode: 'demo' })} />
    </DemoSection>
);

const CommandPalettePreview = () => {
    const [useNameOpenState, setNameOpenState] = useState(false);
    const COMMANDS = [
        { id: 'new', label: 'Nuevo usuario', hint: 'Ctrl+N', group: 'Acciones', keywords: ['crear', 'alta'], onRun: () => {} },
        { id: 'search', label: 'Buscar registros', hint: '/', group: 'Navegación', keywords: ['buscar', 'filtro'], onRun: () => {} },
        { id: 'settings', label: 'Abrir configuración', hint: 'Ctrl+,', group: 'Navegación', keywords: ['ajustes'], onRun: () => {} },
    ];
    return (
        <DemoSection title="CommandPalette" description="Abrí la paleta, escribí y navegá con ↑ ↓ Enter Esc.">
            <AppButton icon={<FaSearch />} onClick={() => setNameOpenState(true)}>Abrir paleta</AppButton>
            <AppCommandPalette isOpen={useNameOpenState} onClose={() => setNameOpenState(false)} commands={COMMANDS} />
        </DemoSection>
    );
};

const ConfirmPreview = () => (
    <DemoSection title="Confirm" description="Motores disponibles y confirmación real del host global.">
        <div className={`${BLOCK}__row`}>
            <AppButton tone="danger" onClick={() => confirm.danger?.('¿Eliminar registro?', 'Este es un ejemplo seed. ¿Continuar?')}>Probar confirmación</AppButton>
            {CONFIRM_TEMPLATES.map((item) => <Chip key={item.id}>{item.label}</Chip>)}
        </div>
    </DemoSection>
);

const DetailPreview = () => (
    <DemoSection title="Detail" description="Vista de lectura con contenido seed.">
        <DetailView title="Ana Torres" subtitle="Perfil de usuario ficticio" status={<StatusBadge status="active" />} actions={<AppButton size="sm" icon={<FaEdit />}>Editar</AppButton>}>
            <DetailSection title="Información personal" columns={3}>
                <DetailItem label="Nombre" value="Ana Torres" /><DetailItem label="Correo" value="ana@example.com" /><DetailItem label="Teléfono" value="+54 221 555-0198" />
                <DetailItem label="Ciudad" value="La Plata" /><DetailItem label="Rol" value="Administradora" /><DetailItem label="Alta" value="04/10/2026" />
            </DetailSection>
        </DetailView>
    </DemoSection>
);

const FiltersPreview = () => {
    const [useNameSearchState, setNameSearchState] = useState('');
    const [useNameStatusState, setNameStatusState] = useState(null);
    const [useNameTypeState, setNameTypeState] = useState(null);
    const FILTERS = [
        { key: 'status', label: 'Estado', type: 'select', value: useNameStatusState, onChange: setNameStatusState, options: [{ value: 'active', label: 'Activo' }, { value: 'pending', label: 'Pendiente' }] },
        { key: 'type', label: 'Tipo', type: 'select', value: useNameTypeState, onChange: setNameTypeState, options: [{ value: 'ui', label: 'UI' }, { value: 'template', label: 'Template' }] },
    ];
    return <DemoSection title="Filters"><FilterBar search={useNameSearchState} onSearch={setNameSearchState} filters={FILTERS} onClearAll={() => { setNameSearchState(''); setNameStatusState(null); setNameTypeState(null); }} /></DemoSection>;
};

const FontsPreview = () => (
    <DemoSection title="Fonts" description="Todos los presets tipográficos registrados.">
        <div className={`${BLOCK}__font_grid`}>
            {FONT_TEMPLATES.map((font) => (
                <article key={font.id} className={`${BLOCK}__font_card`}>
                    <span>{font.label}</span><strong style={{ fontFamily: font.title }}>Aa Título de ejemplo</strong><p style={{ fontFamily: font.base }}>Texto base para verificar legibilidad y jerarquía.</p>
                </article>
            ))}
        </div>
    </DemoSection>
);

const FormPreview = () => {
    const [useNameValuesState, setNameValuesState] = useState({
        name: 'Gastón Demo', email: 'demo@example.com', age: '33', bio: 'Texto seed para probar el textarea.',
        role: 'admin', active: true, notifications: true, level: 'senior', range: 64, rating: 4,
        tags: ['React', 'Node.js'], color: '#6c63ff', duration: 95, monthYear: { month: 10, year: 2026 },
        city: 'La Plata', password: 'Demo1234!', weekdays: ['mon', 'wed', 'fri'],
    });
    const [useNameWizardState, setNameWizardState] = useState(1);
    const onField = (key) => (event) => setNameValuesState((prev) => ({ ...prev, [key]: event?.target ? (event.target.type === 'checkbox' ? event.target.checked : event.target.value) : event }));
    const OPTIONS = [{ value: 'admin', label: 'Administrador' }, { value: 'editor', label: 'Editor' }, { value: 'viewer', label: 'Lector' }];
    return (
        <>
            <DemoSection title="Form · estructura y campos" description="Campos reales con estado seed editable.">
                <Form onSubmit={() => {}}>
                    <FormSection title="Datos básicos" description="Probá escribir, seleccionar y cambiar valores.">
                        <FieldInput id="demo-name" label="Nombre" value={useNameValuesState.name} onChange={onField('name')} isRequired />
                        <FieldInput id="demo-email" label="Correo" type="email" value={useNameValuesState.email} onChange={onField('email')} />
                        <FieldNumber id="demo-age" label="Edad" value={useNameValuesState.age} onChange={onField('age')} min={18} max={99} />
                        <FieldSelect id="demo-role" label="Rol" value={useNameValuesState.role} onChange={onField('role')} options={OPTIONS} />
                        <FieldTextarea id="demo-bio" label="Descripción" value={useNameValuesState.bio} onChange={onField('bio')} maxLength={220} span={2} />
                        <FieldAutocomplete id="demo-city" label="Ciudad" value={useNameValuesState.city} onChange={onField('city')} options={[{ label: 'La Plata' }, { label: 'Buenos Aires' }, { label: 'Mar del Plata' }, { label: 'Rosario' }]} />
                        <FieldPasswordStrength id="demo-password" value={useNameValuesState.password} onChange={onField('password')} />
                    </FormSection>
                    <FormSection title="Controles" columns={2}>
                        <FieldSwitch id="demo-active" label="Cuenta activa" isChecked={useNameValuesState.active} onChange={onField('active')} />
                        <FieldCheckbox id="demo-notifications" label="Recibir notificaciones" checked={useNameValuesState.notifications} value={useNameValuesState.notifications} onChange={onField('notifications')} />
                        <FieldRadioGroup name="demo-level" label="Nivel" value={useNameValuesState.level} onChange={onField('level')} options={[{ value: 'junior', label: 'Junior' }, { value: 'semi', label: 'Semi Senior' }, { value: 'senior', label: 'Senior' }]} />
                        <FieldSegmented id="demo-segment" label="Vista" value="cards" onChange={() => {}} options={[{ value: 'list', label: 'Lista' }, { value: 'cards', label: 'Cards' }]} />
                        <FieldRange id="demo-range" label="Progreso" value={useNameValuesState.range} onChange={onField('range')} min={0} max={100} />
                        <FieldRating id="demo-rating" label="Valoración" value={useNameValuesState.rating} onChange={onField('rating')} />
                        <FieldTags id="demo-tags" label="Tecnologías" value={useNameValuesState.tags} onChange={(value) => setNameValuesState((prev) => ({ ...prev, tags: value }))} suggestions={['React', 'Node.js', 'PHP', 'NestJS']} />
                        <FieldColor id="demo-color" label="Color" value={useNameValuesState.color} onChange={onField('color')} />
                        <FieldDuration id="demo-duration" label="Duración" value={useNameValuesState.duration} onChange={(value) => setNameValuesState((prev) => ({ ...prev, duration: value }))} />
                        <FieldMonthYear id="demo-month" label="Período" value={useNameValuesState.monthYear} onChange={(value) => setNameValuesState((prev) => ({ ...prev, monthYear: value }))} />
                        <FieldWeekdays id="demo-days" label="Días" value={useNameValuesState.weekdays} onChange={(value) => setNameValuesState((prev) => ({ ...prev, weekdays: value }))} span={2} />
                    </FormSection>
                    <FormActions><FormButton type="button" variant="secondary">Cancelar</FormButton><FormButton type="submit">Guardar seed</FormButton></FormActions>
                </Form>
            </DemoSection>
            <DemoSection title="FormWizard" description="Wizard navegable con progreso seed.">
                <FormWizard currentStep={useNameWizardState} maxVisitedStep={2} onStepChange={setNameWizardState} steps={[{ id: 1, title: 'Cuenta', description: 'Datos de acceso.' }, { id: 2, title: 'Perfil', description: 'Información personal.' }, { id: 3, title: 'Confirmar', description: 'Revisión final.' }]}>
                    <SeedInspector title="Paso seleccionado" value={{ currentStep: useNameWizardState + 1, status: 'demo' }} />
                </FormWizard>
            </DemoSection>
            <DemoSection title="Form · inventario avanzado" description="Estos campos también forman parte del boilerplate. Los que dependen de archivos, cámara, mapa o video se representan sin activar hardware/servicios externos.">
                <div className={`${BLOCK}__capability_grid`}>
                    {['FieldOtp', 'FieldMasked', 'FieldAvatar', 'FieldSlug', 'FieldCode', 'FieldNumberUnit', 'FieldIconPicker', 'FieldSignature', 'FieldEmoji', 'FieldMentions', 'FieldImages', 'FieldRepeater', 'FieldTransfer', 'FieldKeyValue', 'FieldTree', 'FieldSchedule', 'FieldAddress', 'FieldCreditCard', 'FieldSortable', 'FieldSelectSearch', 'FieldMultiSelect', 'FieldSearchAsync', 'FieldDate', 'FieldDateRange', 'FieldPhone', 'FieldCurrency', 'FieldFile', 'FieldRichText', 'FieldMap', 'FieldVideoUrl', 'FieldImageCrop', 'FieldQrScanner', 'FieldQrGenerator', 'FieldVideoUpload', 'FieldChapters', 'FieldSubtitles', 'FieldCurriculum'].map((name) => <div key={name}><strong>{name}</strong><span>Disponible · seed seguro</span></div>)}
                </div>
            </DemoSection>
        </>
    );
};

const GalleryPreview = () => <DemoSection title="Gallery" description="Galería seed; hacé clic en una imagen para abrir el Lightbox."><AppGallery items={GALLERY_ITEMS} columns={4} ratio="4/3" /></DemoSection>;

const InlineEditPreview = () => {
    const [useNameValueState, setNameValueState] = useState('Título editable seed');
    return <DemoSection title="InlineEdit" description="Hacé clic y editá el valor."><InlineEdit value={useNameValueState} onSave={setNameValueState} /></DemoSection>;
};

const KanbanPreview = () => {
    const COLUMNS = [{ id: 'todo', label: 'Pendiente', tone: 'neutral', limit: 4 }, { id: 'doing', label: 'En progreso', tone: 'accent', limit: 3 }, { id: 'done', label: 'Finalizado', tone: 'ok' }];
    const BOARD = useKanban({ cards: [
        { id: 'c1', columnId: 'todo', title: 'Diseñar dashboard', meta: 'UI · Alta', order: 1000, tone: 'warn' },
        { id: 'c2', columnId: 'todo', title: 'Validar formulario', meta: 'QA', order: 2000 },
        { id: 'c3', columnId: 'doing', title: 'Integrar API', meta: 'Backend', order: 1000, tone: 'accent' },
        { id: 'c4', columnId: 'done', title: 'Crear boilerplate', meta: 'Core', order: 1000, tone: 'ok' },
    ] });
    return <DemoSection title="Kanban" description="Arrastrá tarjetas o cambialas de columna con el select."><AppKanban board={BOARD} columns={COLUMNS} onAddCard={() => {}} /></DemoSection>;
};

const KitPreview = () => (
    <>
        <DemoSection title="Kit · presentación">
            <div className={`${BLOCK}__row`}><Badge>Neutral</Badge><Badge tone="accent">Accent</Badge><Badge tone="ok">OK</Badge><Badge tone="danger">Danger</Badge></div>
            <div className={`${BLOCK}__row`}><Chip>React</Chip><Chip tone="ok">Reusable</Chip><Avatar name="Gastón Bailador" /><AvatarGroup people={[{ name: 'Ana' }, { name: 'Bruno' }, { name: 'Carla' }, { name: 'Diego' }, { name: 'Eva' }]} /></div>
            <Card title="Card reutilizable" description="Vista base del sistema de templates." footer="Footer del componente">Contenido interno de la tarjeta con datos seed.</Card>
            <Divider>Separador</Divider>
            <Accordion defaultOpen={['catalog']} items={[{ id: 'catalog', title: '¿Qué es este catálogo?', content: 'Una vista navegable y funcional de los componentes reutilizables del boilerplate.' }, { id: 'seed', title: '¿Tiene datos?', content: 'Sí. Todos los demos usan información ficticia para probar estados reales.' }]} />
            <Tooltip text="Tooltip del kit"><span className={`${BLOCK}__tooltip_target`}>Pasá por acá</span></Tooltip>
        </DemoSection>
        <DemoSection title="Kit · feedback y navegación">
            <div className={`${BLOCK}__row`}><Spinner /><Progress value={68} /><Skeleton width="180px" /><SkeletonRow columns={3} /></div>
            <EmptyState title="Sin resultados" description="Ejemplo de estado vacío." size="sm" />
            <Tabs items={[{ value: 'uno', label: 'General' }, { value: 'dos', label: 'Detalles' }]} value="uno" />
            <Breadcrumbs items={[{ label: 'Inicio', href: '/' }, { label: 'Templates' }, { label: 'Kit' }]} />
            <Pagination page={2} total={8} /><Stepper current={1} steps={[{ label: 'Inicio' }, { label: 'Datos' }, { label: 'Confirmación' }]} />
        </DemoSection>
    </>
);

const MetricsPreview = () => <DemoSection title="Metrics"><MetricGrid columns={4}><MetricCard label="Usuarios" value="1.248" trend={12.4} /><MetricCard label="Pendientes" value="38" tone="warn" trend={-4.2} /><MetricCard label="Conversión" value="72" unit="%" tone="ok" trend={3.1} /><MetricCard label="Ingresos" value="$ 84.200" tone="accent" trend={8.8} /></MetricGrid></DemoSection>;

const ModalPreview = () => {
    const [useNameOpenState, setNameOpenState] = useState(false);
    const [useNameModeState, setNameModeState] = useState('top');

    const cb__openModalCallback = (mode) => {
        setNameModeState(mode);
        setNameOpenState(true);
    };

    return (
        <DemoSection title="Modal" description="Probá las tres disposiciones principales sin salir del catálogo.">
            <div className={`${BLOCK}__row`}>
                <AppButton onClick={() => cb__openModalCallback('top')}>Modal superior</AppButton>
                <AppButton variant="outline" onClick={() => cb__openModalCallback('center')}>Modal centrado</AppButton>
                <AppButton variant="outline" onClick={() => cb__openModalCallback('full')}>Pantalla completa</AppButton>
            </div>
            <AppModal
                isOpen={useNameOpenState}
                onClose={() => setNameOpenState(false)}
                title="Editar usuario seed"
                description="Vista de demostración con contenido suficiente para comprobar distribución, scroll y acciones."
                size={useNameModeState === 'full' ? 'full' : 'xl'}
                position={useNameModeState === 'top' ? 'top' : 'center'}
                footer={
                    <>
                        <AppButton variant="ghost" onClick={() => setNameOpenState(false)}>Cancelar</AppButton>
                        <AppButton onClick={() => setNameOpenState(false)}>Guardar cambios</AppButton>
                    </>
                }
            >
                <div className={`${BLOCK}__modal_seed`}>
                    <div><strong>Ana Torres</strong><p>ana@example.com</p><StatusBadge status="active" /></div>
                    <DetailView title="Perfil seed" subtitle="Contenido de prueba para el modal">
                        <DetailSection title="Información" columns={3}>
                            <DetailItem label="Rol" value="Administradora" />
                            <DetailItem label="Ciudad" value="La Plata" />
                            <DetailItem label="Estado" value="Activo" />
                            <DetailItem label="Último acceso" value="04/10/2026 08:40" />
                            <DetailItem label="Equipo" value="Producto" />
                            <DetailItem label="Proyecto" value="BoilerPlate Demo" />
                        </DetailSection>
                    </DetailView>
                    <SeedInspector title="Datos que recibiría la vista" value={{ id: 18, name: 'Ana Torres', role: 'admin', status: 'active', city: 'La Plata' }} />
                </div>
            </AppModal>
        </DemoSection>
    );
};

const PageHeaderPreview = () => (
    <DemoSection title="PageHeader"><PageHeader title="Usuarios" subtitle="Gestión de personas del sistema" description="Cabecera reusable para pantallas y módulos." breadcrumbs={[{ label: 'Inicio', href: '/' }, { label: 'Administración' }, { label: 'Usuarios' }]} status={<StatusBadge status="active" />} meta={<PageHeaderMeta items={[{ label: 'Total', value: '1.248' }, { label: 'Activos', value: '1.032' }]} />} actions={<><AppButton icon={<FaPlus />}>Nuevo usuario</AppButton><AppButton variant="outline">Exportar</AppButton></>} /></DemoSection>
);

const PdfPreview = () => (
    <DemoSection title="PDF" description="Configuración seed de una futura exportación sin descargar archivos automáticamente.">
        <div className={`${BLOCK}__feature_card`}><FaFilePdf /><div><strong>Reporte de usuarios</strong><span>A4 · Vertical · 5 registros · encabezado + pie</span></div><Badge tone="accent">PDF</Badge></div>
        <SeedInspector title="PDF_APPEARANCE_DEFAULT · ejemplo" value={{ format: 'a4', orientation: 'portrait', title: 'Reporte de usuarios', fileName: 'usuarios-demo.pdf', rows: TABLE_ROWS.length }} />
    </DemoSection>
);

const PopoverPreview = () => {
    const REF__useRefButton = useRef(null);
    const [useNameOpenState, setNameOpenState] = useState(false);
    return (
        <DemoSection title="Popover" description="Menú flotante anclado a un botón real.">
            <AppButton ref={REF__useRefButton} variant="outline" onClick={() => setNameOpenState((prev) => !prev)}>Abrir acciones</AppButton>
            <AppPopover isOpen={useNameOpenState} onClose={() => setNameOpenState(false)} anchorRef={REF__useRefButton} role="menu" width={230}>
                <PopoverTitle>Acciones del registro</PopoverTitle><PopoverItem icon={<FaEye />}>Ver detalle</PopoverItem><PopoverItem icon={<FaEdit />}>Editar</PopoverItem><PopoverDivider /><PopoverItem tone="danger">Eliminar</PopoverItem>
            </AppPopover>
        </DemoSection>
    );
};

const ReadOnlyPreview = () => {
    const [useNameOpenState, setNameOpenState] = useState(false);
    const [useNameSectionState, setNameSectionState] = useState('general');
    const NAV = [{ id: 'general', label: 'General', count: 4 }, { id: 'history', label: 'Historial', count: 3 }, { id: 'files', label: 'Archivos', count: 2 }];
    return (
        <>
            <DemoSection title="ReadOnly"><ReadOnlyInfo heading="Usuario seed" createdAt="2026-09-18T10:30:00" updatedAt="2026-10-04T08:15:00" /><ReadOnlySectionNav items={NAV} activeId={useNameSectionState} onSelect={setNameSectionState} /><AppButton icon={<FaEye />} onClick={() => setNameOpenState(true)}>Abrir lectura profunda</AppButton></DemoSection>
            <ReadOnlyModalShell isOpen={useNameOpenState} onClose={() => setNameOpenState(false)} icon={<FaFolderOpen />} title="Manual de onboarding" currentLabel="Sección 2/4" navigation={<ReadOnlySectionNav items={NAV} activeId={useNameSectionState} onSelect={setNameSectionState} />} meta={<ReadOnlyInfo createdAt="2026-09-18T10:30:00" updatedAt="2026-10-04T08:15:00" />}><h2>Contenido seed</h2><p>Este es un ejemplo completo del modo lectura sin modificar ningún registro real.</p></ReadOnlyModalShell>
        </>
    );
};

const RichTextEditorDemo = ({ item }) => {
    const INITIAL = item.format === 'markdown'
        ? '# Demo de contenido\n\nProbá **negrita**, _cursiva_, listas y enlaces.\n\n- Item seed uno\n- Item seed dos'
        : '<h2>Contenido seed editable</h2><p>Probá <strong>negrita</strong>, <em>cursiva</em>, listas y enlaces desde la barra del editor.</p><ul><li>Item seed uno</li><li>Item seed dos</li></ul>';
    const [useNameValueState, setNameValueState] = useState(INITIAL);
    const Component = item.component;

    return (
        <article className={`${BLOCK}__rte_demo`}>
            <div className={`${BLOCK}__rte_head`}>
                <div><strong>{item.label}</strong><p>{item.description}</p></div>
                <Badge tone={item.format === 'html' ? 'accent' : 'neutral'}>{item.format}</Badge>
            </div>
            <Component {...item.props} value={useNameValueState}
                onChange={(next) => setNameValueState(next?.target !== undefined ? next.target.value : next)}
                placeholder="Escribí contenido de demostración..." minHeight={180} />
            <details className={`${BLOCK}__rte_value`}>
                <summary>Ver valor persistido</summary>
                <pre>{useNameValueState}</pre>
            </details>
        </article>
    );
};

const RichTextPreview = () => (
    <DemoSection title="RichText" description="Editores reales y editables: Quill, TipTap y Markdown con contenido seed.">
        <div className={`${BLOCK}__rte_stack`}>
            {RICHTEXT_TEMPLATES.map((item) => <RichTextEditorDemo key={item.id} item={item} />)}
        </div>
    </DemoSection>
);

const SaveBarPreview = () => {
    const [useNameDirtyState, setNameDirtyState] = useState(true);
    const BAR = { isDirty: useNameDirtyState, isSaving: false, error: null, savedAt: useNameDirtyState ? null : new Date(), onDiscard: () => setNameDirtyState(false), onSave: () => setNameDirtyState(false) };
    return (
        <DemoSection title="SaveBar" description="Se muestra dentro del demo sin fijarla al viewport.">
            <div className={`${BLOCK}__row`}><AppButton variant="outline" onClick={() => setNameDirtyState(true)}>Marcar cambios</AppButton><SaveBarStamp bar={BAR} /></div>
            <SaveBar bar={BAR} isFixed={false} message="Hay cambios seed sin guardar" />
        </DemoSection>
    );
};

const ScreenPreview = () => (
    <>
        <DemoSection title="ScreenState" description="Estado base con texto, código y acción visibles.">
            <ScreenState code="200" title="Estado genérico" description="Base reutilizable para mensajes de pantalla." tone="accent" actions={<AppButton size="sm">Continuar</AppButton>} />
        </DemoSection>
        <DemoSection title="Estados especializados" description="Todos renderizados en modo contenido, con textos seed explícitos para no depender de traducciones externas.">
            <div className={`${BLOCK}__screen_grid`}>
                <ScreenNotFound isFullScreen={false} title="Página no encontrada" description="La ruta solicitada no existe o fue movida." homeLabel="Volver al inicio" />
                <ScreenForbidden isFullScreen={false} title="Acceso restringido" description="Tu usuario no tiene permiso para abrir este recurso." requiredPermission="users.read" backLabel="Volver" />
                <ScreenError isFullScreen={false} title="No se pudo cargar" description="Ocurrió un error seed controlado para comprobar este estado." error={new Error('Error seed controlado')} isDetailed={false} onRetry={() => {}} retryLabel="Reintentar" />
                <ScreenMaintenance isFullScreen={false} title="Mantenimiento programado" description="Estamos aplicando mejoras. La aplicación volverá a estar disponible pronto." estimatedTime="20 minutos" actions={<AppButton size="sm" variant="outline">Ver estado</AppButton>} />
            </div>
        </DemoSection>
        <DemoSection title="DataNotice" description="Avisos de datos con contenido visible y sin depender de backend.">
            <DataNotice title="Datos de demostración" description="Este bloque usa información ficticia y no afecta datos reales." />
        </DemoSection>
    </>
);

const SeoPreview = () => {
    const ORG = onBuildOrganizationJsonLd({ name: 'BoilerPlate Demo', url: 'https://example.com', logo: 'https://example.com/logo.png', sameAs: ['https://example.com/social'] });
    const CRUMBS = onBuildBreadcrumbJsonLd([{ label: 'Inicio', href: '/' }, { label: 'Templates', href: '/templates-demo' }], 'https://example.com');
    return <DemoSection title="SEO" description="Resultado real de los builders usando datos seed."><div className={`${BLOCK}__code_grid`}><SeedInspector title="Organization JSON-LD" value={ORG} /><SeedInspector title="Breadcrumb JSON-LD" value={CRUMBS} /></div></DemoSection>;
};

const ShortcutsPreview = () => {
    const [useNameOpenState, setNameOpenState] = useState(false);
    const SHORTCUTS = [{ id: 'search', label: 'Buscar', keys: 'ctrl+k', group: 'General' }, { id: 'new', label: 'Nuevo registro', keys: 'ctrl+n', group: 'Registros' }, { id: 'save', label: 'Guardar', keys: 'ctrl+s', group: 'Registros' }];
    return <DemoSection title="Shortcuts" description="Representación y ayuda navegable."><div className={`${BLOCK}__row`}><ShortcutKeys keys="ctrl+k" /><ShortcutKeys keys="ctrl+shift+p" /><AppButton icon={<FaKeyboard />} onClick={() => setNameOpenState(true)}>Ver todos</AppButton></div><ShortcutsHelp isOpen={useNameOpenState} onClose={() => setNameOpenState(false)} shortcuts={SHORTCUTS} /></DemoSection>;
};

const SplitPreview = () => (
    <DemoSection title="SplitPane" description="Arrastrá el divisor para cambiar el tamaño."><div className={`${BLOCK}__split_host`}><SplitPane initial={42} min={25} max={75}><div className={`${BLOCK}__split_panel`}><strong>Panel A</strong><p>Listado seed</p></div><div className={`${BLOCK}__split_panel`}><strong>Panel B</strong><p>Detalle seed seleccionado</p></div></SplitPane></div></DemoSection>
);

const StatusPreview = () => <DemoSection title="Status" description="Variantes y estados semánticos."><div className={`${BLOCK}__row`}>{['active', 'pending', 'inactive', 'approved', 'rejected', 'draft', 'archived', 'unknown'].map((status) => <StatusBadge key={status} status={status} />)}</div></DemoSection>;

const TablePreview = () => {
    const [useNameViewState, setNameViewState] = useState('list');
    const [useNamePerPageState, setNamePerPageState] = useState(12);
    return (
        <>
            <DemoSection title="DataTable" description="Tabla seed completa."><DataTable columns={TABLE_COLUMNS} rows={TABLE_ROWS} title="Usuarios demo" isExportable={false} isColumnToggle={false} isDensityToggle={false} isPaginated={false} hasViewToggle={false} /></DemoSection>
            <DemoSection title="Controles de tabla"><div className={`${BLOCK}__row`}><TableViewToggle value={useNameViewState} onChange={setNameViewState} /><TablePerPage value={useNamePerPageState} onChange={setNamePerPageState} totalItems={TABLE_ROWS.length} /><TableActionMenu items={[{ id: 'view', label: 'Ver detalle', onClick: () => {} }, { id: 'edit', label: 'Editar', onClick: () => {} }]} /></div></DemoSection>
            <DemoSection title="TableGrid"><TableGrid rows={TABLE_ROWS} columns={TABLE_COLUMNS} columnsCount={3} renderGridItem={(row) => <Card title={row.name} description={row.role}><p>{row.email}</p><StatusBadge status={row.status} /></Card>} /></DemoSection>
            <DemoSection title="Table templates"><div className={`${BLOCK}__template_grid`}>{TABLE_TEMPLATES.map((item) => <article key={item.id}><strong>{item.label}</strong><code>{item.id}</code></article>)}</div></DemoSection>
        </>
    );
};

const TimelinePreview = () => (
    <DemoSection title="Timeline"><Timeline items={[{ id: 1, title: 'Creado', description: 'Se creó el registro seed.', date: '09:00', tone: 'accent' }, { id: 2, title: 'Revisado', description: 'QA validó los datos.', date: '11:30', tone: 'warn' }, { id: 3, title: 'Aprobado', description: 'El flujo finalizó correctamente.', date: '14:10', tone: 'ok', isActive: true }]} /><Divider /><Timeline variant="cards"><TimelineItem title="Evento manual" description="También puede componerse con children." date="Hoy" tone="accent" /></Timeline></DemoSection>
);

const TreePreview = () => <DemoSection title="Tree"><AppTree nodes={TREE_NODES} defaultExpanded={['app', 'web', 'templates', 'ui']} /></DemoSection>;

const UploaderPreview = () => {
    const BASIC_UPLOADER = useUploader({ isAuto: false, maxFiles: 4, maxSize: 5 * 1024 * 1024, accept: ['image/*', '.pdf', '.txt'] });
    const VIEWER_UPLOADER = useUploader({ isAuto: false, maxFiles: 6, maxSize: 5 * 1024 * 1024, accept: ['image/*', '.pdf', '.txt', '.md'] });
    const REF__useRefSeeded = useRef(false);

    useEffect(() => {
        if (REF__useRefSeeded.current || typeof File === 'undefined') return;
        REF__useRefSeeded.current = true;

        const SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="1000" height="600"><rect width="1000" height="600" fill="#4f5bd5"/><circle cx="730" cy="170" r="120" fill="#ffffff" fill-opacity=".15"/><circle cx="240" cy="470" r="170" fill="#000000" fill-opacity=".12"/><text x="500" y="325" fill="white" font-family="Arial" font-size="58" text-anchor="middle">Imagen seed</text></svg>`;
        const IMAGE_FILE = new File([SVG], 'imagen-seed.svg', { type: 'image/svg+xml' });
        const TEXT_FILE = new File(['BoilerPlate WebApp\n\nArchivo seed para probar el lector del uploader.\nEstado: OK'], 'notas-seed.txt', { type: 'text/plain' });

        BASIC_UPLOADER.onAdd([IMAGE_FILE]);
        VIEWER_UPLOADER.onAdd([IMAGE_FILE, TEXT_FILE]);
    }, [BASIC_UPLOADER, VIEWER_UPLOADER]);

    return (
        <>
            <DemoSection title="Uploader básico" description="Carga simple con acciones de ver y descargar por archivo.">
                <AppUploader uploader={BASIC_UPLOADER} accept={['image/*', '.pdf', '.txt']} variant="compact"
                    title="Seleccionar archivo" hint="Imagen, PDF o TXT · máximo 5 MB" />
            </DemoSection>
            <DemoSection title="Uploader con visor" description="Incluye archivos seed: probá Ver imagen, Ver archivo y Descargar.">
                <AppUploader uploader={VIEWER_UPLOADER} accept={['image/*', '.pdf', '.txt', '.md']}
                    title="Archivos con visor" hint="Imagen, PDF, TXT o Markdown · máximo 5 MB" />
                <SeedInspector title="Estado de la cola" value={{ total: VIEWER_UPLOADER.total, done: VIEWER_UPLOADER.done, progress: VIEWER_UPLOADER.progress, isUploading: VIEWER_UPLOADER.isUploading }} />
            </DemoSection>
        </>
    );
};



const CHAT_USERS = [
    {
        id: 'u1', name: 'Gastón Bailador', role: 'Producto', online: true,
        team: 'Producto', email: 'gaston@scooldata.test', phone: '+54 221 555-0101', location: 'La Plata, Buenos Aires',
        description: 'Coordina producto, definición funcional y seguimiento general del equipo.',
    },
    {
        id: 'u2', name: 'Ana Torres', role: 'Diseño', online: true,
        team: 'Diseño UX/UI', email: 'ana@scooldata.test', phone: '+54 221 555-0102', location: 'La Plata, Buenos Aires',
        description: 'Diseñadora de producto enfocada en experiencia de usuario, prototipos e interfaz visual.',
    },
    {
        id: 'u3', name: 'Martín López', role: 'Backend', online: false,
        team: 'Tecnología', email: 'martin@scooldata.test', phone: '+54 11 5555-0103', location: 'Buenos Aires, Argentina',
        description: 'Desarrollador backend responsable de APIs, servicios y lógica de negocio.',
    },
    {
        id: 'u4', name: 'Sofía Díaz', role: 'QA', online: true,
        team: 'Quality Assurance', email: 'sofia@scooldata.test', phone: '+54 221 555-0104', location: 'La Plata, Buenos Aires',
        description: 'Analista QA enfocada en pruebas funcionales, regresión y validación de entregas.',
    },
    {
        id: 'u5', name: 'Diego Fernández', role: 'Soporte', online: false,
        team: 'Soporte', email: 'diego@scooldata.test', phone: '+54 351 555-0105', location: 'Córdoba, Argentina',
        description: 'Atiende incidencias, seguimiento de usuarios y documentación operativa.',
    },
    {
        id: 'u6', name: 'Lucía Gómez', role: 'Marketing', online: true,
        team: 'Marketing', email: 'lucia@scooldata.test', phone: '+54 341 555-0106', location: 'Rosario, Santa Fe',
        description: 'Gestiona campañas, contenidos y comunicación de producto.',
    },
];

const CHAT_CONVERSATIONS = [
    {
        id: 'direct-ana', memberIds: ['u1', 'u2'], unread: 2, mentions: 0, pinned: true, invitations: [],
        messages: [
            { id: 'm1', senderId: 'u2', text: '¿Pudiste revisar el nuevo dashboard?', createdAt: '2026-10-04T08:12:00-03:00', status: 'read' },
            { id: 'm2', senderId: 'u1', text: 'Sí. Me gusta mucho más así. Ajustaría solamente el espacio del header.', createdAt: '2026-10-04T08:14:00-03:00', status: 'read' },
            { id: 'm3', senderId: 'u1', text: 'Te paso también el brief para que quede adjunto.', createdAt: '2026-10-04T08:15:00-03:00', status: 'delivered', attachments: [{ id: 'a1', name: 'brief-dashboard.txt', size: 148, type: 'text/plain', url: 'data:text/plain;charset=utf-8,Brief%20seed%20del%20dashboard%20para%20el%20template%20de%20chat.' }] },
            { id: 'm4', senderId: 'u2', text: 'Perfecto, lo dejo listo y te aviso.', createdAt: '2026-10-04T08:16:00-03:00', status: 'sent' },
        ],
    },
    {
        id: 'group-product', name: 'Equipo Producto', type: 'group', memberIds: ['u1', 'u2', 'u3', 'u4'], adminIds: ['u1'], unread: 3, mentions: 1, pinned: false, pinnedMessageId: 'g2', typingUserIds: ['u2'], invitations: ['nuevo.integrante@example.com'],
        messages: [
            { id: 'g0', senderId: 'u2', text: 'Dejo el resumen funcional de ayer.', createdAt: '2026-10-03T17:20:00-03:00', status: 'read', attachments: [{ id: 'ga0', name: 'resumen-funcional.txt', size: 214, type: 'text/plain', url: 'data:text/plain;charset=utf-8,Resumen%20funcional%20seed%20del%20grupo%20Producto.' }] },
            { id: 'g1', senderId: 'u3', text: 'API de notificaciones lista en staging.', createdAt: '2026-10-04T07:40:00-03:00', status: 'read', attachments: [{ id: 'ga1', name: 'api-notificaciones.json', size: 326, type: 'application/json', url: 'data:application/json;charset=utf-8,%7B%22status%22%3A%22staging%22%2C%22seed%22%3Atrue%7D' }] },
            { id: 'g2', senderId: 'u4', text: '@Gastón QA empieza las pruebas en unos minutos.', createdAt: '2026-10-04T07:43:00-03:00', status: 'read' },
            { id: 'g3', senderId: 'u1', text: 'Genial. Si aparece algo crítico lo vemos por acá.', createdAt: '2026-10-04T07:46:00-03:00', status: 'read' },
        ],
    },
    {
        id: 'direct-martin', memberIds: ['u1', 'u3'], unread: 1, mentions: 0, pinned: false, invitations: [],
        messages: [
            { id: 'b1', senderId: 'u3', text: 'Te dejé documentado el endpoint nuevo.', createdAt: '2026-10-03T18:22:00-03:00', status: 'sent' },
        ],
    },
];

const ChatPreview = () => {
    const NAVIGATE = useNavigate();
    const CHAT = useChat({ conversations: CHAT_CONVERSATIONS, currentUserId: 'u1', initialConversationId: 'direct-ana' });

    return (
        <AppModal
            isOpen={true}
            onClose={() => NAVIGATE('/templates-demo')}
            title="Chat"
            description="Conversaciones directas y grupales con datos seed."
            size="full"
            position="center"
            className={`${BLOCK}__chat_modal`}
        >
            <AppChat chat={CHAT} currentUserId="u1" users={CHAT_USERS} title="Conversaciones" />
        </AppModal>
    );
};


const SUPPORT_USERS = [
    { id: 'customer-1', name: 'Daiana Paredes', role: 'Cliente', email: 'daiana@example.test' },
    { id: 'customer-2', name: 'Nicolás Ferreyra', role: 'Cliente', email: 'nicolas@example.test' },
];

const SUPPORT_AGENTS = [
    { id: 'agent-1', name: 'Gastón Bailador', role: 'Soporte N2' },
    { id: 'agent-2', name: 'Carla Ruiz', role: 'Soporte N1' },
    { id: 'agent-3', name: 'Martín López', role: 'Infraestructura' },
];

const SUPPORT_TICKETS = [
    {
        id: 'ticket-1042', code: '#SD-1042', subject: 'No puedo ingresar al campus', status: 'open', priority: 'urgent',
        category: 'Acceso y cuenta', type: 'Incidente', level: 'N2', channel: 'Web', impact: 'Alto', urgency: 'Urgente', visibility: 'internal', slaTarget: '2h', assigneeId: 'agent-1', unread: 2, typingUserIds: ['customer-1'],
        updatedAt: '2026-10-05T01:44:00-03:00', createdAt: '2026-10-05T01:12:00-03:00',
        requester: { name: 'Daiana Paredes', role: 'Estudiante', email: 'daiana@example.test', phone: '+54 221 555-0110', userCode: 'USR-2841', city: 'La Plata', country: 'Argentina', accountStatus: 'Activa', lastAccess: '05/10/2026, 00:48' },
        device: { os: 'Windows 11', browser: 'Chrome 154', model: 'Notebook Lenovo' },
        sla: { firstResponse: '8 min restantes', resolution: '1 h 42 min' },
        tags: ['login', 'campus', 'urgente'],
        messages: [
            { id: 'st1', senderId: 'customer-1', text: 'Hola, intento ingresar al campus y vuelve al login. Ya restablecí la contraseña.', createdAt: '2026-10-05T01:12:00-03:00' },
            { id: 'st2', senderId: 'agent-1', text: 'Gracias. Voy a revisar la sesión y el último acceso de tu cuenta.', createdAt: '2026-10-05T01:18:00-03:00' },
            { id: 'st3', senderId: 'agent-1', text: 'Se detectó una sesión vencida en otro dispositivo. Dejé registrada la evidencia para el equipo.', internal: true, createdAt: '2026-10-05T01:20:00-03:00', attachments: [{ id: 'log-1', name: 'session-log.txt', size: 418, type: 'text/plain', url: 'data:text/plain;charset=utf-8,session%20expired%20seed' }] },
        ],
        events: [{ id: 'ev1', text: 'Ticket creado desde el formulario de soporte.', createdAt: '2026-10-05T01:12:00-03:00' }],
    },
    {
        id: 'ticket-1038', code: '#SD-1038', subject: 'El pago figura pendiente pero ya fue debitado', status: 'pending', priority: 'high',
        category: 'Facturación', type: 'Problema técnico', level: 'N1', channel: 'Email', impact: 'Medio', urgency: 'Alta', visibility: 'internal', slaTarget: '4h', assigneeId: 'agent-2', unread: 1,
        updatedAt: '2026-10-04T20:10:00-03:00', createdAt: '2026-10-04T18:42:00-03:00',
        requester: { name: 'Nicolás Ferreyra', role: 'Estudiante', email: 'nicolas@example.test', phone: '+54 11 5555-0144', userCode: 'USR-3177', city: 'CABA', country: 'Argentina', accountStatus: 'Activa', lastAccess: '04/10/2026, 18:35' },
        device: { os: 'Android 15', browser: 'Chrome Mobile', model: 'Samsung Galaxy' },
        sla: { firstResponse: 'Cumplido', resolution: '3 h 15 min' },
        tags: ['pago', 'mercadopago'],
        messages: [
            { id: 'st4', senderId: 'customer-2', text: 'Adjunto comprobante. El cobro aparece en mi banco pero la inscripción sigue pendiente.', createdAt: '2026-10-04T18:42:00-03:00', attachments: [{ id: 'pay-1', name: 'comprobante-demo.txt', size: 176, type: 'text/plain', url: 'data:text/plain;charset=utf-8,comprobante%20seed' }] },
            { id: 'st5', senderId: 'agent-2', text: 'Recibido. Estamos validando la acreditación con el proveedor de pagos.', createdAt: '2026-10-04T18:51:00-03:00' },
        ],
        events: [],
    },
    {
        id: 'ticket-1029', code: '#SD-1029', subject: 'La videollamada se corta cada pocos minutos', status: 'resolved', priority: 'normal',
        category: 'Rendimiento', type: 'Incidente', level: 'Infraestructura', channel: 'Chat', impact: 'Medio', urgency: 'Normal', visibility: 'internal', slaTarget: '8h', assigneeId: 'agent-3', unread: 0,
        updatedAt: '2026-10-04T12:33:00-03:00', createdAt: '2026-10-04T10:03:00-03:00',
        requester: { name: 'Daiana Paredes', role: 'Estudiante', email: 'daiana@example.test', phone: '+54 221 555-0110', userCode: 'USR-2841', city: 'La Plata', country: 'Argentina', accountStatus: 'Activa', lastAccess: '05/10/2026, 00:48' },
        device: { os: 'macOS 15', browser: 'Safari 19', model: 'MacBook Air' },
        sla: { firstResponse: 'Cumplido', resolution: 'Resuelto' },
        tags: ['video', 'red'],
        messages: [{ id: 'st6', senderId: 'customer-1', text: 'Después de cambiar de red quedó estable. Gracias.', createdAt: '2026-10-04T12:30:00-03:00' }],
        events: [{ id: 'ev2', text: 'Ticket marcado como resuelto por Martín López.', createdAt: '2026-10-04T12:33:00-03:00' }],
    },
];

const SupportChatPreview = () => {
    const NAVIGATE = useNavigate();
    const SUPPORT = useSupportChat({ tickets: SUPPORT_TICKETS, currentUserId: 'agent-1', initialTicketId: 'ticket-1042' });

    return (
        <AppModal
            isOpen={true}
            onClose={() => NAVIGATE('/templates-demo')}
            title="Soporte técnico"
            description="Mesa de ayuda reusable con tickets, SLA, estados, prioridad, notas internas, adjuntos y gestión de agentes."
            size="full"
            position="center"
            className={`${BLOCK}__chat_modal`}
        >
            <AppSupportChat support={SUPPORT} currentUserId="agent-1" users={SUPPORT_USERS} agents={SUPPORT_AGENTS} title="Mesa de ayuda" />
        </AppModal>
    );
};


const CALLCENTER_CONTACTS = [
    { id: 'lead-1', name: 'Mariana Suárez', role: 'Prospecto', email: 'mariana@example.test' },
    { id: 'lead-2', name: 'Federico Gómez', role: 'Cliente', email: 'federico@example.test' },
    { id: 'lead-3', name: 'Lucía Benítez', role: 'Prospecto', email: 'lucia@example.test' },
];

const CALLCENTER_AGENTS = [
    { id: 'cc-agent-1', name: 'Gastón Bailador', role: 'Operador Senior' },
    { id: 'cc-agent-2', name: 'Carla Ruiz', role: 'Supervisora' },
    { id: 'cc-agent-3', name: 'Martín López', role: 'Calidad' },
];

const CALLCENTER_CALLS = [
    {
        id: 'call-2048', code: '#CC-2048', subject: 'Renovación plan Premium', status: 'connected', priority: 'high',
        category: 'Renovación', type: 'Llamada saliente', level: 'Operador', channel: 'Discador', impact: 'Caliente', urgency: 'Alta',
        visibility: 'internal', slaTarget: '2h', assigneeId: 'cc-agent-1', unread: 2, typingUserIds: ['lead-1'],
        campaign: 'Renovaciones Octubre', queue: 'Ventas AR', disposition: 'Interesada - evaluando oferta', callbackAt: '2026-10-05T18:00',
        updatedAt: '2026-10-05T11:38:00-03:00', createdAt: '2026-10-05T11:12:00-03:00',
        requester: { name: 'Mariana Suárez', role: 'Prospecto', email: 'mariana@example.test', phone: '+54 221 555-0177', userCode: 'LEAD-8842', city: 'La Plata', country: 'Argentina', accountStatus: 'Lead caliente', lastAccess: '05/10/2026, 11:10' },
        sla: { firstResponse: '1 min 12 s', resolution: '08 min 34 s' },
        tags: ['renovacion', 'premium', 'callback'],
        messages: [
            { id: 'cc1', senderId: 'lead-1', text: 'Hola, me llamaron por la renovación. Quería saber si mantienen el descuento.', createdAt: '2026-10-05T11:12:00-03:00' },
            { id: 'cc2', senderId: 'cc-agent-1', text: 'Sí, podemos mantener el beneficio si renovás este mes. Te paso el detalle.', createdAt: '2026-10-05T11:14:00-03:00' },
            { id: 'cc3', senderId: 'cc-agent-1', text: 'Cliente interesada. Pide callback después de las 18:00 para confirmar.', internal: true, createdAt: '2026-10-05T11:20:00-03:00' },
        ],
        events: [
            { id: 'ccev1', text: 'Contacto ingresado desde campaña Renovaciones Octubre.', createdAt: '2026-10-05T11:12:00-03:00' },
            { id: 'ccev2', text: 'Llamada conectada - duración 08:34.', createdAt: '2026-10-05T11:21:00-03:00' },
        ],
    },
    {
        id: 'call-2047', code: '#CC-2047', subject: 'Consulta por cobertura', status: 'callback', priority: 'normal',
        category: 'Ventas', type: 'Callback', level: 'Operador', channel: 'Teléfono', impact: 'Tibio', urgency: 'Normal',
        visibility: 'internal', slaTarget: '4h', assigneeId: 'cc-agent-2', unread: 1,
        campaign: 'Leads Web', queue: 'Inbound', disposition: 'Solicita llamada más tarde', callbackAt: '2026-10-05T16:30',
        updatedAt: '2026-10-05T10:45:00-03:00', createdAt: '2026-10-05T10:02:00-03:00',
        requester: { name: 'Federico Gómez', role: 'Cliente', email: 'federico@example.test', phone: '+54 11 5555-0166', userCode: 'CLI-3318', city: 'CABA', country: 'Argentina', accountStatus: 'Cliente activo', lastAccess: '05/10/2026, 09:58' },
        sla: { firstResponse: '42 s', resolution: '04 min 10 s' },
        tags: ['cobertura', 'callback'],
        messages: [
            { id: 'cc4', senderId: 'lead-2', text: 'Ahora estoy trabajando, ¿me pueden llamar después de las 16:30?', createdAt: '2026-10-05T10:02:00-03:00' },
            { id: 'cc5', senderId: 'cc-agent-2', text: 'Perfecto, te agendo para las 16:30.', createdAt: '2026-10-05T10:03:00-03:00' },
        ],
        events: [{ id: 'ccev3', text: 'Callback agendado para las 16:30.', createdAt: '2026-10-05T10:05:00-03:00' }],
    },
    {
        id: 'call-2046', code: '#CC-2046', subject: 'Lead campaña referidos', status: 'queued', priority: 'urgent',
        category: 'Ventas', type: 'Llamada saliente', level: 'Operador', channel: 'Importado', impact: 'Prioritario', urgency: 'Inmediata',
        visibility: 'internal', slaTarget: '1h', assigneeId: 'cc-agent-1', unread: 0,
        campaign: 'Referidos Q4', queue: 'Hot Leads', disposition: 'Pendiente primer intento',
        updatedAt: '2026-10-05T09:44:00-03:00', createdAt: '2026-10-05T09:40:00-03:00',
        requester: { name: 'Lucía Benítez', role: 'Prospecto', email: 'lucia@example.test', phone: '+54 351 555-0149', userCode: 'LEAD-9012', city: 'Córdoba', country: 'Argentina', accountStatus: 'Lead prioritario', lastAccess: 'Sin interacción previa' },
        sla: { firstResponse: '12 min restantes', resolution: 'Sin llamada' },
        tags: ['referido', 'hot-lead'],
        messages: [{ id: 'cc6', senderId: 'cc-agent-1', text: 'Lead importado. Pendiente primer contacto.', internal: true, createdAt: '2026-10-05T09:41:00-03:00' }],
        events: [{ id: 'ccev4', text: 'Contacto asignado a cola Hot Leads.', createdAt: '2026-10-05T09:40:00-03:00' }],
    },
];

const CallCenterChatPreview = () => {
    const NAVIGATE = useNavigate();
    const CALL_CENTER = useCallCenterChat({ calls: CALLCENTER_CALLS, currentUserId: 'cc-agent-1', initialCallId: 'call-2048' });

    return (
        <AppModal
            isOpen={true}
            onClose={() => NAVIGATE('/templates-demo')}
            title="CALL CENTER"
            description="Chat operativo para llamadas, campañas, colas, callbacks, tipificaciones, contactos y seguimiento de agentes."
            size="full"
            position="center"
            className={`${BLOCK}__chat_modal`}
        >
            <AppCallCenterChat chat={CALL_CENTER} currentUserId="cc-agent-1" users={CALLCENTER_CONTACTS} agents={CALLCENTER_AGENTS} title="Centro de llamadas" />
        </AppModal>
    );
};

const WizardPreview = () => {
    const WIZARD_STEPS = [
        { id: 'account', shortLabel: 'Acceso', title: 'Cuenta', description: 'Datos principales de acceso.' },
        { id: 'profile', shortLabel: 'Perfil', title: 'Perfil', description: 'Información personal y profesional.' },
        { id: 'preferences', shortLabel: 'Opciones', title: 'Preferencias', description: 'Configuración inicial de la experiencia.' },
        { id: 'review', shortLabel: 'Final', title: 'Confirmación', description: 'Revisión antes de finalizar.' },
    ];
    const WIZARD = useWizard({ steps: WIZARD_STEPS });
    const [useNameCompletedState, setNameCompletedState] = useState(false);

    const STEP_CONTENT = [
        { title: 'Cuenta preparada', text: 'gastón.demo@example.com', detail: 'Perfil seed con acceso habilitado.' },
        { title: 'Perfil profesional', text: 'Software Developer', detail: 'La Plata · Argentina' },
        { title: 'Preferencias', text: 'Modo oscuro · Español', detail: 'Notificaciones activadas.' },
        { title: 'Todo listo', text: '4 pasos completos', detail: 'Podés finalizar el asistente.' },
    ];
    const current = STEP_CONTENT[WIZARD.currentStep];

    return (
        <DemoSection title="Wizard" description="Asistente reutilizable, navegable y controlado por useWizard.">
            <AppWizard
                title="Configuración inicial"
                description="Ejemplo seed de un flujo multipaso reutilizable."
                steps={WIZARD_STEPS}
                currentStep={WIZARD.currentStep}
                maxVisitedStep={WIZARD.maxVisitedStep}
                onStepChange={WIZARD.goTo}
                onBack={WIZARD.back}
                onNext={WIZARD.next}
                onComplete={() => setNameCompletedState(true)}
                completeLabel="Confirmar"
            >
                <div className={`${BLOCK}__wizard_seed`}>
                    <strong>{current.title}</strong>
                    <span>{current.text}</span>
                    <p>{current.detail}</p>
                    {useNameCompletedState && <Badge tone="ok">Wizard finalizado</Badge>}
                </div>
            </AppWizard>
        </DemoSection>
    );
};

const VirtualPreview = () => {
    const ITEMS = Array.from({ length: 300 }, (_, index) => ({ id: index + 1, name: `Registro virtual ${String(index + 1).padStart(3, '0')}`, status: index % 3 === 0 ? 'active' : index % 3 === 1 ? 'pending' : 'inactive' }));
    const COLUMNS = [{ key: 'id', label: '#', width: '70px' }, { key: 'name', label: 'Nombre' }, { key: 'status', label: 'Estado', width: '130px', render: (row) => <StatusBadge status={row.status} size="sm" /> }];
    return <><DemoSection title="VirtualList" description="300 filas seed sin renderizarlas todas a la vez."><VirtualList items={ITEMS} height={300} rowHeight={44} renderItem={(item) => <div className={`${BLOCK}__virtual_row`}><strong>{item.name}</strong><StatusBadge status={item.status} size="sm" /></div>} /></DemoSection><DemoSection title="VirtualTable"><VirtualTable rows={ITEMS} columns={COLUMNS} height={320} /></DemoSection></>;
};

const onRenderPreview = (item) => {
    const PREVIEWS = {
        alerts: <AlertsPreview />, button: <ButtonPreview />, calendar: <CalendarPreview />, chat: <ChatPreview />, supportchat: <SupportChatPreview />, callcenterchat: <CallCenterChatPreview />, charts: <ChartsPreview />, codeblock: <CodePreview />,
        commandpalette: <CommandPalettePreview />, confirm: <ConfirmPreview />, detail: <DetailPreview />, filters: <FiltersPreview />, fonts: <FontsPreview />,
        form: <FormPreview />, gallery: <GalleryPreview />, inlineedit: <InlineEditPreview />, kanban: <KanbanPreview />, kit: <KitPreview />,
        metrics: <MetricsPreview />, modal: <ModalPreview />, pageheader: <PageHeaderPreview />, pdf: <PdfPreview />, popover: <PopoverPreview />,
        readonly: <ReadOnlyPreview />, richtext: <RichTextPreview />, savebar: <SaveBarPreview />, screen: <ScreenPreview />, seo: <SeoPreview />,
        shortcuts: <ShortcutsPreview />, split: <SplitPreview />, status: <StatusPreview />, table: <TablePreview />, timeline: <TimelinePreview />,
        tree: <TreePreview />, uploader: <UploaderPreview />, virtual: <VirtualPreview />, wizard: <WizardPreview />,
    };
    return PREVIEWS[item.id];
};

export const DemoTemplatesViewerMain = () => {
    const { templateId } = useParams();
    const [useNameSearchState, setNameSearchState] = useState('');

    const memo__catalogMemo = useMemo(() => {
        const QUERY = useNameSearchState.trim().toLowerCase();
        if (!QUERY) return TEMPLATE_CATALOG;
        return TEMPLATE_CATALOG.filter((item) => item.label.toLowerCase().includes(QUERY) || item.exports.some((name) => name.toLowerCase().includes(QUERY)));
    }, [useNameSearchState]);

    const memo__selectedMemo = useMemo(() => {
        const ID = String(templateId ?? 'kit').toLowerCase();
        return TEMPLATE_CATALOG.find((item) => item.id === ID) ?? TEMPLATE_CATALOG.find((item) => item.id === 'kit') ?? TEMPLATE_CATALOG[0];
    }, [templateId]);

    return (
        <div className={BLOCK}>
            <div className={`${BLOCK}__intro`}>
                <div><span className={`${BLOCK}__eyebrow`}>UI TEST · SEED DATA</span><h1>Demo Templates Viewer</h1><p>Catálogo funcional de componentes, templates y utilidades reutilizables. Todo usa datos ficticios para probar estados reales sin tocar información del proyecto.</p></div>
                <div className={`${BLOCK}__summary`}><strong>{TEMPLATE_CATALOG.length}</strong><span>grupos disponibles</span></div>
            </div>

            <div className={`${BLOCK}__workspace`}>
                <aside className={`${BLOCK}__nav`}>
                    <label htmlFor="template-demo-search">Buscar</label>
                    <input id="template-demo-search" type="search" value={useNameSearchState} onChange={(event) => setNameSearchState(event.target.value)} placeholder="Componente o grupo..." />
                    <div className={`${BLOCK}__nav_list`}>
                        {memo__catalogMemo.map((item) => (
                            <Link key={item.id} to={`/templates-demo/${item.id}`} className={`${BLOCK}__nav_item`} data-active={item.id === memo__selectedMemo.id}>
                                <span>{item.label}</span><small>{item.exports.length}</small>
                            </Link>
                        ))}
                    </div>
                </aside>

                <main className={`${BLOCK}__content`}>
                    <div className={`${BLOCK}__component_head`}>
                        <div><span>App/WEB/_Templates/_UI/{memo__selectedMemo.label}</span><h2>{memo__selectedMemo.label}</h2></div>
                        <Badge tone="accent">{memo__selectedMemo.exports.length} exports</Badge>
                    </div>
                    <Inventory item={memo__selectedMemo} />
                    {onRenderPreview(memo__selectedMemo)}
                </main>
            </div>
        </div>
    );
};

export default DemoTemplatesViewerMain;
